import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Typography,
  Flex,
  Upload,
  theme,
  Divider,
  InputNumber,
  Card,
  Spin,
} from "antd";
import { CheckCircleFilled, EditOutlined, InboxOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import type { FormInstance, UploadFile } from "antd";
import { FieldLabel, HybridCard, SectionHeader, inputStyle } from "../../shared";
import type { ConceptData, QuoteData, QuoteProduct } from "../types";
import type { QuoteTotals } from "../utils/quoteTotals";
import type { ClientData } from "../../clients/types";
import openNotification from "../../../utils/openNotification";
import FormNewProduct from "./FormNewProduct";
import SignaturePad, { type SignaturePadHandle } from "./SignaturePad";
import type { ProductData } from "../../products/types";
import { ArrowUp10 } from "lucide-react";
import { URL_FILES } from "../../../constans/apis";
import { useUploadFile } from "../../../lib/files/hooks/useUploadFile";
import ItemProduct from "../../products/components/ItemProduct";
import ClientFormDrawer from "../../clients/components/ClientFormDrawer";
import { lineSubtotal, resolveQuoteTotals } from "../utils/quoteTotals";

const { TextArea } = Input;
const { Text } = Typography;
const { Dragger } = Upload;

/**
 * Texto de presentación por defecto para nuevas cotizaciones.
 * Se precarga en el campo `quote_wave` y el usuario puede editarlo o borrarlo.
 */
export const DEFAULT_QUOTE_WAVE = `Estimado cliente:

Reciba un cordial saludo. Agradecemos el interés y la confianza depositada en nosotros. A continuación ponemos a su consideración la siguiente propuesta, elaborada conforme a los requerimientos que nos compartió.

Quedamos a sus órdenes para cualquier duda, aclaración o ajuste que requiera.`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface QuoteFormProps {
  form: FormInstance;
  mode: "create" | "edit";
  /** Pre-populated for edit mode */
  initialQuote?: QuoteData;
  /** Líneas de la cotización: de aquí salen el subtotal, el IVA y el total. */
  selectedProducts: QuoteProduct[];
  onProductChange: (p: ProductData | undefined) => void;
  onAmountChange: (id: number, amount: number) => void;
  onRemoveProduct: (id_product: number) => void;
  /**
   * Cambios de la línea (descripción / precio de venta) que solo viven en la
   * cotización: el catálogo de productos nunca se modifica desde aquí.
   */
  onProductLineChange?: (product: QuoteProduct) => void;
  fileList: UploadFile[];
  onFileListChange: (files: UploadFile[]) => void;
  /** Called with the uuid_file_quote value (empty string if no file) after upload */
  onUuidFileReady: (uuid: string) => void;
  clients: ClientData[];
  concepts: ConceptData[];
  isLoadingClients: boolean;
  isLoadingConcepts: boolean;
  isSubmitting: boolean;
  hideConcepts?: boolean;
  hideProducts?: boolean;
  onSubmit: () => void;
  /**
   * Firma (solo modo edición, ver `EditQuotePanel`): el estado vive en el
   * padre porque `handleSubmit` necesita leerlo al guardar.
   */
  signerName?: string;
  onSignerNameChange?: (value: string) => void;
  signaturePadRef?: React.RefObject<SignaturePadHandle | null>;
  isRedrawingSignature?: boolean;
  onToggleRedrawSignature?: (value: boolean) => void;
  currentSignatureSrc?: string;
  isLoadingSignature?: boolean;
  hasExistingSignature?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const QuoteForm: React.FC<QuoteFormProps> = ({
  form,
  mode,
  initialQuote,
  selectedProducts,
  onProductChange,
  onAmountChange,
  onRemoveProduct,
  onProductLineChange,
  fileList,
  onFileListChange,
  clients,
  concepts,
  isLoadingClients,
  isLoadingConcepts,
  isSubmitting,
  onSubmit,
  hideConcepts = true,
  hideProducts = false,
  signerName = "",
  onSignerNameChange,
  signaturePadRef,
  isRedrawingSignature = false,
  onToggleRedrawSignature,
  currentSignatureSrc,
  isLoadingSignature,
  hasExistingSignature,
}) => {
  const { token } = theme.useToken();
  const iStyle = inputStyle(token);

  // Alta rápida de cliente sin salir de la cotización (create y edit).
  const [isClientDrawerOpen, setIsClientDrawerOpen] = useState(false);

  // Los importes SIEMPRE se derivan de las líneas; los campos guardados en la
  // cotización solo reflejan ese cálculo. Si la cotización no tiene líneas se
  // respetan los importes ya guardados para no ponerlos en cero.
  const totals = resolveQuoteTotals({
    products: selectedProducts,
    total_quote: initialQuote?.total_quote,
    iva_quote: initialQuote?.iva_quote,
  });

  useEffect(() => {
    form.setFieldsValue({ total_quote: totals.total, iva_quote: totals.iva });
  }, [form, totals.total, totals.iva]);

  return (
    <Form
      id={mode === "create" ? "new-quote-form" : "edit-quote-form"}
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      {/* ── Client ───────────────────────────────────────────────── */}
      <SectionHeader title="Cliente" />
      <Form.Item
        name="id_client"
        label={<FieldLabel text="Cliente" />}
        rules={[{ required: true, message: "Selecciona un cliente" }]}
        style={{ marginBottom: 0, flex: 1 }}
      >
        <Select
          showSearch
          size="large"
          placeholder="Selecciona un cliente"
          loading={isLoadingClients}
          allowClear
          filterOption={(input, option) =>
            (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={clients.map((c) => ({
            label: c.client_name,
            value: c.id_client,
          }))}
        />
      </Form.Item>
      <Button
        type="dashed"
        htmlType="button"
        icon={<PlusOutlined />}
        onClick={() => setIsClientDrawerOpen(true)}
        style={{ marginTop: 8, borderRadius: token.borderRadius }}
      >
        Agregar cliente
      </Button>
      <ClientFormDrawer
        open={isClientDrawerOpen}
        onClose={() => setIsClientDrawerOpen(false)}
        onCreated={(id_client) => {
          // La lista de clientes ya viene refrescada; se deja seleccionado el nuevo.
          form.setFieldValue("id_client", id_client);
          form.validateFields(["id_client"]).catch(() => { });
        }}
      />
      <Divider style={{ margin: "24px 0" }} />

      {/* ── Quote Details ─────────────────────────────────────────── */}
      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Detalles de la Cotización" />

        <Form.Item
          name="description_quote"
          label={<FieldLabel text="Descripción" />}
          rules={[{ required: true, message: "La descripción es obligatoria" }]}
          style={{ marginBottom: 0 }}
        >
          <TextArea rows={4} placeholder="Descripción de los servicios o productos cotizados" />
        </Form.Item>

        {/* Texto de cortesía: viene precargado en alta y el usuario puede cambiarlo. */}
        <Form.Item
          name="quote_wave"
          label={<FieldLabel text="Texto presentación" />}
          initialValue={mode === "create" ? DEFAULT_QUOTE_WAVE : undefined}
          style={{ marginBottom: 0 }}
        >
          <TextArea rows={6} placeholder="Texto presentación" />
        </Form.Item>
        {!hideConcepts && (
          <Form.Item
            name="id_concept"
            label={<FieldLabel text="Concepto" />}
            rules={[{ required: true, message: "El concepto es obligatorio" }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Select
              size="large"
              placeholder="Selecciona un concepto"
              loading={isLoadingConcepts}
              allowClear
              options={concepts.map((c) => ({
                label: c.concept_name,
                value: c.id_concept,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item name="total_quote" hidden>
          <InputNumber />
        </Form.Item>
        <Form.Item name="iva_quote" hidden>
          <InputNumber />
        </Form.Item>

        <Flex gap={12} wrap>
          <Form.Item
            name="date_quote"
            label={<FieldLabel text="Fecha de Emisión" />}
            rules={[{ required: true, message: "La fecha de emisión es obligatoria" }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <DatePicker
              size="large"
              style={{ ...iStyle, width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="dd/mm/aaaa"
            />
          </Form.Item>
          <Form.Item
            name="date_expiration"
            label={<FieldLabel text="Fecha de Vencimiento" />}
            rules={[{ required: true, message: "La fecha de vencimiento es obligatoria" }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <DatePicker
              size="large"
              style={{ ...iStyle, width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="dd/mm/aaaa"
            />
          </Form.Item>
        </Flex>
      </Flex>

      {/* ── Products ──────────────────────────────────────────────── */}
      {!hideProducts && (
        <section style={{ marginBottom: 28 }}>
          <SectionHeader title="Productos / Servicios" />
          <FormNewProduct
            handleChangeProduct={onProductChange}
            selectedProducts={selectedProducts}
          />
          {/* Se listan en orden inverso: lo último agregado queda hasta arriba.
              El arreglo original conserva el orden de captura (payload y PDF). */}
          {[...selectedProducts].reverse().map((product) => (
            <ItemProduct
              key={product.id_product}
              product={product}
              footer={
                <QuoteLineFields
                  product={product}
                  onAmountChange={onAmountChange}
                  onRemoveProduct={onRemoveProduct}
                  onProductLineChange={onProductLineChange}
                />
              }
            />
          ))}
        </section>
      )}

      {/* Importes calculados a partir de las líneas (no se capturan a mano) */}
      <QuoteTotalsSummary totals={totals} />

      <Divider style={{ margin: "0 0 24px" }} />

      {/* ── Attachment (create only) ───────────────────────────────── */}
      {mode === "create" && (
        <Flex vertical gap={16} style={{ marginBottom: 32 }}>
          <SectionHeader title="Archivo Adjunto (Opcional)" />
          <Form.Item name="file" style={{ marginBottom: 0 }}>
            <Dragger
              name="file"
              accept=".pdf,.xml"
              maxCount={1}
              fileList={fileList}
              beforeUpload={(file) => {
                onFileListChange([file as unknown as UploadFile]);
                form.setFieldValue("file", file);
                return false;
              }}
              onRemove={() => {
                onFileListChange([]);
                form.setFieldValue("file", null);
              }}
              style={{
                borderRadius: token.borderRadiusLG,
                background: token.colorFillQuaternary,
                border: `2px dashed ${token.colorBorderSecondary}`,
                padding: "8px 0",
              }}
            >
              <Flex vertical align="center" gap={8} style={{ padding: "16px 24px" }}>
                <Flex
                  justify="center"
                  align="center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: token.colorPrimaryBg,
                    color: token.colorPrimary,
                    fontSize: 24,
                    marginBottom: 4,
                  }}
                >
                  <InboxOutlined />
                </Flex>
                <Text strong>Subir Documento (PDF/XML)</Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 13, textAlign: "center", maxWidth: 220 }}
                >
                  Arrastra el archivo aquí o haz clic para explorar
                </Text>
              </Flex>
            </Dragger>
          </Form.Item>
        </Flex>
      )}

      {/* ── Firma (solo edición) ─────────────────────────────────────── */}
      {mode === "edit" && (
        <Flex vertical gap={16} style={{ marginBottom: 28 }}>
          <SectionHeader title="Firma" />
          <Card size="small" style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
            <Flex vertical gap={12}>
              <Flex vertical gap={4} style={{ maxWidth: 320 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Firmar como</Text>
                <Input
                  value={signerName}
                  onChange={(e) => onSignerNameChange?.(e.target.value)}
                  placeholder="Nombre del firmante"
                  prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
                />
              </Flex>

              {isLoadingSignature ? (
                <Spin size="small" />
              ) : !isRedrawingSignature && currentSignatureSrc ? (
                <Flex align="center" gap={12}>
                  <div
                    style={{
                      width: 160,
                      height: 80,
                      background: "#fff",
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: token.borderRadius,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={currentSignatureSrc}
                      alt="Firma actual"
                      style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
                    />
                  </div>
                  <Button icon={<EditOutlined />} onClick={() => onToggleRedrawSignature?.(true)}>
                    Cambiar firma
                  </Button>
                </Flex>
              ) : (
                <Flex vertical gap={8} style={{ maxWidth: 420 }}>
                  <SignaturePad ref={signaturePadRef} height={160} />
                  <Flex gap={8}>
                    <Button size="small" onClick={() => signaturePadRef?.current?.clear()}>
                      Limpiar
                    </Button>
                    {hasExistingSignature && (
                      <Button size="small" onClick={() => onToggleRedrawSignature?.(false)}>
                        Cancelar
                      </Button>
                    )}
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Card>
        </Flex>
      )}

      {/* ── Submit ────────────────────────────────────────────────── */}
      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={isSubmitting}
        icon={<CheckCircleFilled />}
        block
        style={{
          height: 54,
          borderRadius: token.borderRadiusLG,
          fontWeight: 800,
          fontSize: 16,
        }}
      >
        {mode === "create" ? "Guardar Cotización" : "Guardar Cambios"}
      </Button>
    </Form>
  );
};

export default QuoteForm;

// ---------------------------------------------------------------------------
// Línea de la cotización: cantidad, precio de venta, descripción, importe y quitar.
// Descripción y precio se capturan por cotización: NO tocan el catálogo.
// ---------------------------------------------------------------------------
const QuoteLineFields: React.FC<{
  product: QuoteProduct;
  onAmountChange: (id: number, amount: number) => void;
  onRemoveProduct: (id_product: number) => void;
  onProductLineChange?: (product: QuoteProduct) => void;
}> = ({ product, onAmountChange, onRemoveProduct, onProductLineChange }) => {
  const { token } = theme.useToken();

  const microLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
  };
  const numFont: React.CSSProperties = {
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
  };

  const canEditLine = !!onProductLineChange;

  return (
    <Flex vertical gap={14}>
      <Flex align="flex-end" justify="space-between" gap={16} wrap>
        <Flex vertical gap={4}>
          <Text type="secondary" style={microLabel}>Cantidad</Text>
          <InputNumber
            min={1}
            precision={0}
            value={product.product_amount}
            onChange={(v) => onAmountChange(product.id_product!, Number(v) || 1)}
            style={{ width: 110, ...numFont }}
          />
        </Flex>

        {canEditLine && (
          <Flex vertical gap={4} style={{ flex: "0 1 200px" }}>
            <Text type="secondary" style={microLabel}>Precio venta</Text>
            <InputNumber
              min={0}
              controls={false}
              value={product.product_price_sale ?? product.product_price}
              prefix={<span style={{ color: token.colorTextTertiary, fontWeight: 600 }}>$</span>}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => Number((v || "").replace(/[^\d.]/g, ""))}
              onChange={(v) =>
                onProductLineChange?.({ ...product, product_price_sale: Number(v) || 0 })
              }
              style={{ width: "100%", ...numFont }}
            />
          </Flex>
        )}

        <Flex vertical gap={4} align="flex-end" style={{ marginLeft: "auto" }}>
          <Text type="secondary" style={microLabel}>Importe</Text>
          <Text strong style={{ fontSize: 18, ...numFont }}>{MXN.format(lineSubtotal(product))}</Text>
        </Flex>

        <Button
          danger
          type="primary"
          ghost
          onClick={() => onRemoveProduct(product.id_product!)}
          style={{ borderRadius: token.borderRadius }}
        >
          Quitar
        </Button>
      </Flex>

      {canEditLine && (
        <Flex vertical gap={4}>
          <Text type="secondary" style={microLabel}>Descripción</Text>
          <TextArea
            value={product.product_description}
            placeholder="Descripción del producto…"
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{ resize: "none" }}
            onChange={(e) =>
              onProductLineChange?.({ ...product, product_description: e.target.value })
            }
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            La descripción y el precio solo se guardan en esta cotización; el catálogo no se modifica.
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Resumen de importes (solo lectura)
// ---------------------------------------------------------------------------
const MXN = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

const QuoteTotalsSummary: React.FC<{ totals: QuoteTotals }> = ({ totals }) => {
  const { token } = theme.useToken();

  const row = (label: string, value: number, strong = false) => (
    <Flex justify="space-between" align="center">
      <Text type={strong ? undefined : "secondary"} strong={strong} style={{ fontSize: strong ? 15 : 13 }}>
        {label}
      </Text>
      <Text strong={strong} style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 800 : 600 }}>
        {MXN.format(value)}
      </Text>
    </Flex>
  );

  return (
    <Flex
      vertical
      gap={8}
      style={{
        padding: "14px 16px",
        borderRadius: token.borderRadiusLG,
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Text
        type="secondary"
        style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}
      >
        Importes (calculados)
      </Text>
      {row("Subtotal", totals.subtotal)}
      {row("IVA", totals.iva)}
      <Divider style={{ margin: "4px 0" }} />
      {row("Total", totals.total, true)}
      <Text type="secondary" style={{ fontSize: 11 }}>
        Se calculan con las líneas de la cotización: cantidad × precio de venta, más el IVA de cada producto.
      </Text>
    </Flex>
  );
};
